/**
 * Whether an account is usable, as opposed to what it is called or how it is
 * grouped.
 *
 * Deliberately its own tiny module rather than part of `utils/accounts`. That
 * module is partially mocked by a dozen specs, so anything imported from it by
 * a widely-rendered component breaks the moment a mock omits the new export.
 * Nothing mocks this file, and there is no reason it ever should.
 */

/**
 * True when an account is broken at the account level, whatever its platform:
 * revoked, not in an OK state, or flagged for reconnection by the token
 * refresher. This is the ACCOUNT-level rule, used by the red dot in the
 * account pickers.
 *
 * SKE-5376. `phoneStateIgnored` is `isInstagramPhoneStateIgnored`'s answer for
 * the account. When true, `legit` and the phone `status` stop counting and only
 * the token refresher's flag does.
 *
 * The media feeds deliberately do NOT use it. See
 * `igAccountNeedsMediaReconnect` below, which is narrower on purpose. The two
 * answer different questions and are meant to differ.
 */
export const accountIsBrokenAtAccountLevel = (
  account: {
    // `AccountType.legit` is nullable, so the structural type has to be too.
    legit?: boolean | null;
    status?: string;
    promptApiReconnect?: boolean;
  },
  phoneStateIgnored: boolean
): boolean =>
  !!account.promptApiReconnect ||
  (!phoneStateIgnored && (account.legit === false || account.status !== "ok"));

/**
 * Whether an Instagram account needs reconnecting *for the Graph-backed media
 * feeds* (hashtags, tagged-in, creators).
 *
 * Narrower than both the account picker's disconnected rule and the
 * account-level predicate above, and deliberately so. These feeds need exactly
 * one thing: a usable Instagram insights token. They do not need the inbox
 * messaging scope, and they do not care whether the account can post.
 *
 * In particular this must NOT key on `status` or `legit`. An insights-only
 * account carries `status: "insightsOnly"` and `legit: false` while holding the
 * very token these feeds use, and an account under review is `legit: false`
 * too. Treating either as broken would tell people to reconnect for a feature
 * that works, while the request underneath succeeded.
 *
 * The server is the authority (it checks the stored token, then Meta). This is
 * the local predicate that lets the UI say so before the round trip; the
 * server's verdict is ORed in at the call site.
 */
export const igAccountNeedsMediaReconnect = (
  account?: {
    promptApiReconnect?: boolean;
    status?: string;
    igTokenAddedAt?: string | boolean;
  } | null
): boolean => {
  if (!account) return false;
  // Deliberately NOT `status === "failed"`, despite the server's publishing
  // rule using it. `failed` is not only a dead token: PAUSING an account sets
  // it ("You set this account to be paused."), and `setAccountToFailState`
  // sets it for publish failures that say nothing about credentials, such as
  // rejected media or a quota. None of those stop the INSIGHTS token these
  // feeds read, so keying on it would blank a working feed and tell the user
  // to reconnect an account they paused on purpose.
  //
  // `insightsOnly` and `legit: false` are excluded for the same reason: those
  // accounts hold exactly the token these feeds use. The server's verdict is
  // what catches a genuinely dead token; this predicate is the local hint.
  return !!account.promptApiReconnect || !account.igTokenAddedAt;
};
